<script setup lang="ts">
import { ref } from 'vue';
import { Switch, SwitchDescription, SwitchGroup, SwitchLabel } from '@headlessui/vue';
import { useToast } from 'vue-toastification';
import { useRouter } from 'vue-router';
import type { Iitems } from '@/modules/shared/interfaces/inputMultiple';
import { stringReg } from '@/modules/shared/utils/reg';
import ButtonG from '@/modules/shared/components/ButtonComponent.vue';
import SpinnerComponent from '@/modules/shared/components/SpinnerComponent.vue';
import InputMultiple from '@/modules/shared/components/InputMultiple.vue';
import useMailboxes from '@/modules/settings/composables/useMailboxes';
import useUserInfo from '@/modules/shared/composable/useUserInfo';
import ToastComponent from '@/modules/shared/components/ToastComponent.vue';
import { CheckCircleIcon } from '@heroicons/vue/20/solid';

const toast = useToast();
const router = useRouter();

const { createMailboxes, mailboxes } = useMailboxes();
const { userInfo } = useUserInfo();

const enabled = ref(false);
const email = ref('');
const validating = ref('before');
const emails = ref<Iitems[]>([]);
const validEmails = ref<string[]>([]);
const invalidEmails = ref<{ email: string; message: string }[]>([]);
const finishRequest = ref(false);
const clicked = ref(false);

const socket = ref();
const isConnected = ref(false);
const reconnectAttempts = ref(0);
const maxReconnectAttempts = 10;
const validatingEmails = ref<{ email: string; message: string }[]>([]);

interface DataDict {
    email: string;
    code: string;
    status: string;
    message: string;
}

const isDisabled = () => !(!email.value.length && emails.value.length > 0) || emails.value.length + mailboxes.value.length > userInfo.value.maxMailboxes;

const reconnect = () => {
    reconnectAttempts.value++;

    window.setTimeout(() => {
        socketConnect();
    }, 3000);
};

const socketConnect = () => {
    socket.value = new WebSocket(import.meta.env.VITE_BACKEND_WEBSOCKET_HOST);

    socket.value.onopen = () => {
        socket.value.send(JSON.stringify({ type: 'auth', token: localStorage.getItem('userToken') }));
        isConnected.value = true;

        const mailboxes: string[] = [];
        emails.value.forEach((email) => mailboxes.push(email.item));
        socket.value.send(JSON.stringify({ type: 'command', command: 'check_mailboxes', mailboxes }));

        socket.value.onmessage = (event: { data: string }) => {
            const data: { type: string; data_dict: DataDict } = JSON.parse(event.data);
            let bool = false;
            invalidEmails.value.forEach((item) => {
                if (item.email === data.data_dict?.email) bool = true;
            });
            validatingEmails.value.forEach((item) => {
                if (item.email === data.data_dict?.email) item.message = data.data_dict?.message;
            });
            if (data.data_dict?.status === 'OK' && validEmails.value.indexOf(data.data_dict?.email) === -1) validEmails.value.push(data.data_dict.email);
            else if (data.data_dict?.status === 'ERROR' && !bool) invalidEmails.value.push({ email: data.data_dict.email, message: data.data_dict.message });
        };
    };

    socket.value.onclose = () => {
        console.warn('❌ WebSocket desconectado.');
        if (isConnected.value && router.currentRoute.value.fullPath.includes('add')) {
            isConnected.value = false;
            if (reconnectAttempts.value < maxReconnectAttempts) {
                reconnect();
            }
        }
    };
};

const ConfirmAddMail = () => {
    if (validating.value === 'before') {
        validatingEmails.value = [];
        if (import.meta.env.VITE_BUGSNAG_KEY) {
            validating.value = 'validating';
            socketConnect();
            emails.value.forEach((em) => {
                if (!validatingEmails.value.find((email) => email.email === em.item)) validatingEmails.value.push({ email: em.item, message: '' });
            });
        } else {
            validating.value = 'after';
            emails.value.forEach((em) => {
                if (!validEmails.value.find((email) => email === em.item)) validEmails.value.push(em.item);
            });
        }
    } else {
        clicked.value = true;
        const now = new Date();
        if (enabled.value) {
            now.setFullYear(now.getFullYear() - 2);
        }

        if (validEmails.value.length) {
            const emailData = {
                emails: validEmails.value,
                data_from: new Date(now).toJSON().slice(0, 10)
            };
            if (import.meta.env.VITE_BUGSNAG_KEY) socket.value.close();
            isConnected.value = false;
            createMailboxes(emailData).then(async (newMailboxes) => {
                newMailboxes.forEach((mailbox) => {
                    mailboxes.value.push(mailbox);
                });
                finishRequest.value = true;
                router.back();
                const m = validEmails.value.length;
                toast.success(
                    {
                        component: ToastComponent,
                        props: {
                            title: `Mailbox${m > 1 ? 'es' : ''} added`,
                            messenges: 'This change will be reflected in your dashboard tomorrow at 00:01 local time'
                        },
                        options: { icon: CheckCircleIcon }
                    },
                    {
                        timeout: 10000
                    }
                );
            });
        }
    }
};

const handleClose = () => {
    validating.value = 'before';
    isConnected.value = false;
    socket.value.close();
    validEmails.value = [];
    invalidEmails.value = [];
};

const listCopy = () => {
    const array: string[] = [];
    invalidEmails.value.forEach((item) => array.push(item.email));
    const text = array.join(',');
    navigator.clipboard
        .writeText(text)
        .then(() => {
            alert('List copied: ' + text);
        })
        .catch(() => {
            alert('Error copying the list');
        });
};
</script>

<template>
    <div v-if="validating === 'before'" class="flex flex-col bg-white border border-gray-200 rounded-lg w-full mx-auto">
        <p class="text-gray-900 font-semibold leading-8 text-lg p-6 border-b border-gray-200">Add mailboxes</p>
        <div class="p-6 border-b border-gray-200 overflow-auto">
            <p class="text-md leading-5 font-normal text-gray-700 pb-8">Enter the email addresses you want to track</p>
            <div>
                <InputMultiple
                    placeholder="Enter email addresses, one per line or separated by commas"
                    maxHeight="h-32"
                    v-model:text="email"
                    v-model:items="emails"
                    :reg="(e: string) => !stringReg.test(e)"
                    :allowedDomains="[]"
                    :onlyExistEmails="[]"
                />
                <span v-if="emails.length + mailboxes.length > userInfo.maxMailboxes" class="text-red-400 block mt-1 text-left text-xs font-inter font-medium">
                    You're trying to add {{ emails.length }} mailboxes, but you only have {{ userInfo.maxMailboxes - mailboxes.length }} slots remaining. Remove some mailboxes or contact your Customer
                    Success Manager to increase your limit.
                </span>
            </div>
            <div class="pt-8">
                <SwitchGroup as="div" class="flex items-center">
                    <Switch
                        v-model="enabled"
                        :class="[
                            enabled ? 'bg-indigo-600' : 'bg-gray-400',
                            'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-0 focus:ring-offset-0'
                        ]"
                    >
                        <span
                            aria-hidden="true"
                            :class="[
                                enabled ? 'translate-x-5' : 'translate-x-0',
                                'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                            ]"
                        />
                    </Switch>
                    <span class="flex grow flex-col ml-3.5">
                        <SwitchLabel as="span" class="text-sm font-medium leading-5 text-gray-900" passive>Load two years of historical email data</SwitchLabel>
                        <SwitchDescription as="span" class="text-sm leading-5 text-gray-500">If unselected, we’ll only start tracking email data from now on.</SwitchDescription>
                    </span>
                </SwitchGroup>
            </div>
        </div>
        <div class="flex justify-end gap-2 p-4">
            <ButtonG hierarcy="outline" text="Cancel" @click="router.back()" />
            <ButtonG :disabled="isDisabled()" hierarcy="primary" text="Add mailboxes" @click="ConfirmAddMail" />
        </div>
    </div>
    <div
        v-else-if="validating === 'validating' && !(validEmails.length + invalidEmails.length === validatingEmails.length)"
        class="flex flex-col bg-white border border-gray-200 rounded-lg w-full mx-auto p-6"
    >
        <p class="text-gray-900 font-semibold leading-8 text-lg py-6 border-b border-gray-200">Validating mailboxes</p>
        <div class="py-6 border-b border-gray-200 overflow-auto">
            <p class="text-md leading-5 font-normal text-gray-700">Please wait while we validate your mailboxes</p>
        </div>
        <div class="w-full py-6 flex flex-col justify-center">
            <div class="h-2 w-full rounded-full bg-gray-200 mb-4">
                <div class="h-full bg-primary-600 rounded-full" :style="`width: ${((validEmails.length + invalidEmails.length) / validatingEmails.length) * 100}% !important;`"></div>
            </div>
            <p class="text-md leading-5 font-normal text-gray-700 text-center">Validating... {{ (((validEmails.length + invalidEmails.length) / validatingEmails.length) * 100).toFixed(0) }}%</p>
        </div>
        <div class="border border-gray-200 rounded-lg w-full max-h-96 min-h-64 overflow-y-scroll">
            <div v-for="address of validatingEmails" :key="address.email" class="p-2 border-b border-gray-200 flex justify-between items-center">
                <p class="font-medium">{{ address.email }}</p>
                <p v-if="address.message" class="text-xs" :class="!address.message.includes('Invalid') && address.message.includes('valid') ? 'text-green-600' : 'text-red-500'">
                    {{ address.message }}
                </p>
                <SpinnerComponent v-else class="h-4 w-4 fill-blue-600" />
            </div>
        </div>
    </div>
    <div
        v-else-if="(validEmails.length + invalidEmails.length === validatingEmails.length && validatingEmails.length) || validating === 'after'"
        class="flex flex-col bg-white border border-gray-200 rounded-lg w-full mx-auto"
    >
        <p class="text-gray-900 font-semibold leading-8 text-lg p-6 border-b border-gray-200">Validation results</p>
        <div class="p-6 border-b border-gray-200">
            <p class="text-md leading-5 font-normal text-gray-700 pb-8">Review the validation results before adding mailboxes</p>
            <div class="flex gap-2">
                <div class="border border-gray-200 rounded-lg w-3/4 min-h-64 max-h-96">
                    <div class="flex bg-green-200 text-green-700 p-2 font-medium">
                        Valid mailboxes <span class="ml-1"> ({{ validEmails.length }})</span>
                    </div>
                    <div class="overflow-y-scroll max-h-84 py-2">
                        <div v-for="(address, i) of validEmails" :key="i">
                            <p class="p-1 font-medium">{{ address }}</p>
                        </div>
                    </div>
                </div>
                <div class="border border-gray-200 rounded-lg w-3/4 min-h-64 max-h-96">
                    <div class="flex bg-red-200 text-red-600 p-2 w-full justify-between">
                        <div class="flex font-medium">
                            Invalid mailboxes<span class="ml-1">({{ invalidEmails.length }})</span>
                        </div>
                        <button class="fill-red-600 cursor-pointer font-medium text-xs hover:bg-red-300 rounded text-end w-fit p-1" @click="listCopy">Copy list</button>
                    </div>
                    <div class="overflow-y-scroll max-h-84 py-2">
                        <div v-for="(item, i) of invalidEmails" :key="i" class="flex justify-between items-center">
                            <p class="p-1 font-medium truncate ...">{{ item.email }}</p>
                            <p class="text-xs text-red-500 p-1">{{ item.message }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="flex justify-end gap-2 p-4">
            <ButtonG hierarcy="outline" text="Cancel" @click="handleClose" />
            <ButtonG :disabled="!validEmails.length || clicked" hierarcy="primary" text="Add Valid Mailboxes" :is-request="true" :finish-request="finishRequest" @click="ConfirmAddMail" />
        </div>
    </div>
</template>
